import { SHStack } from "@components/design-systems/sh-stack";
import {
  CalendarPickerView,
  DatePickerProps,
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import {
  SHTextField,
  SHTextFieldProps,
} from "@components/design-systems/sh-text-field";
import { CalendarSVG } from "@components/svgs/calendar";
import { forwardRef } from "react";
import { CalendarCheckedSVG } from "@components/svgs/calendar-checked";
import { useToggle } from "react-use";
import { SxProps, Theme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRangeLimits } from "@pages/suppliers/constants";

export interface DatePickerFieldProps
  extends Omit<DatePickerProps<Date | null, Date | null>, "renderInput"> {
  textFieldProps?: Omit<SHTextFieldProps, "value">;
  views?: CalendarPickerView[];
  inputFormat?: string;
  sxTextField?: SxProps<Theme>;
  openOnClick?: boolean;
}
export const ViewModeTableDatePicker = forwardRef(
  (
    {
      inputFormat,
      textFieldProps,
      value,
      views = ["year", "month"],
      sxTextField,
      openOnClick = false,
      ...others
    }: DatePickerFieldProps,
    ref: any,
  ): JSX.Element => {
    const [openCalendar, toggleOpenCalendar] = useToggle(false);
    return (
      <SHStack>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          localeText={{
            clearButtonLabel: "Reset",
          }}
        >
          <DesktopDatePicker
            {...others}
            PopperProps={{
              modifiers: [
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["top", "bottom"],
                  },
                },
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                  },
                },
              ],
            }}
            views={views}
            inputFormat={inputFormat}
            value={value}
            ref={ref}
            components={{
              OpenPickerIcon:
                textFieldProps?.error || !value
                  ? CalendarSVG
                  : CalendarCheckedSVG,
            }}
            renderInput={(params) => (
              <SHTextField
                {...params}
                {...textFieldProps}
                size="small"
                sx={{
                  ...sxTextField,
                  caretColor: openOnClick ? "transparent" : "initial",
                }}
                onPaste={(e) => {
                  openOnClick && e.preventDefault();
                }}
                autoComplete={openOnClick ? "off" : "on"}
                onClick={(e) => {
                  if (others.disabled) e.preventDefault();
                  else toggleOpenCalendar(true);
                }}
                onKeyDown={(e) => {
                  if (openOnClick) e.preventDefault();
                }}
              />
            )}
            componentsProps={{
              actionBar: openOnClick
                ? {
                    actions: ["clear"],
                    style: {
                      height: "35px",
                    },
                    title: "Reset",
                  }
                : {},
            }}
            open={openOnClick ? openCalendar : undefined}
            onClose={() => toggleOpenCalendar(false)}
            maxDate={DateRangeLimits.maxDate}
            minDate={DateRangeLimits.minDate}
          />
        </LocalizationProvider>
      </SHStack>
    );
  },
);
